import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, MapPin, Rocket, FileText, Star } from "lucide-react";
import { GigmaWordmark } from "../components/Logo";
import { GooglePreviewCard } from "../components/GooglePreviewCard";
import { PublishAssistant } from "../components/PublishAssistant";
import { ReviewKit } from "../components/ReviewKit";
import { useWizard, BUSINESS_TYPES } from "../lib/wizardStore";

export default function Completion() {
  const [params] = useSearchParams();
  const { state, setPayment } = useWizard();
  const success = params.get("success") === "true";

  // Stripe Payment Link redirects here with ?success=true after payment.
  // No backend session verification is used in the static deployment.
  const [status] = useState(success ? "paid" : "ok");
  const sessionId = null;
  const error = "";

  // Persist paid state on successful return
  React.useEffect(() => {
    if (success) setPayment({ paid: true });
  }, [success, setPayment]);

  const bt = BUSINESS_TYPES.find((b) => b.id === state.businessType);
  const [tab, setTab] = useState("assistant");

  return (
    <div className="min-h-screen bg-slate-50/60">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="completion-home"><GigmaWordmark size={28} /></Link>
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-800">Home</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-16">
        {/* Status banner */}
        <StatusBanner status={status} error={error} sessionId={sessionId} />

        {status === "paid" || !sessionId ? (
          <div className="mt-10">
            {/* Tabs */}
            <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 mb-8">
              <button
                type="button"
                onClick={() => setTab("assistant")}
                data-testid="tab-publish-assistant"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === "assistant" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
              >
                <Rocket className="w-3.5 h-3.5" /> Publish Assistant
              </button>
              <button
                type="button"
                onClick={() => setTab("summary")}
                data-testid="tab-profile-summary"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === "summary" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
              >
                <FileText className="w-3.5 h-3.5" /> My Profile Summary
              </button>
              <button
                type="button"
                onClick={() => setTab("reviews")}
                data-testid="tab-review-kit"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === "reviews" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
              >
                <Star className="w-3.5 h-3.5" /> Review Kit
              </button>
            </div>

            {tab === "assistant" ? (
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7">
                  <PublishAssistant state={state} bt={bt} onOpenReviews={() => setTab("reviews")} />
                </div>
                <div className="lg:col-span-5">
                  <div className="lg:sticky lg:top-24">
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                      What you're publishing
                    </div>
                    <GooglePreviewCard />
                  </div>
                </div>
              </div>
            ) : tab === "reviews" ? (
              <div className="max-w-4xl">
                <ReviewKit />
              </div>
            ) : (
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7">
                  <ProfileSummary state={state} bt={bt} />
                </div>
                <div className="lg:col-span-5">
                  <div className="lg:sticky lg:top-24">
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                      Your finalized profile
                    </div>
                    <GooglePreviewCard />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusBanner({ status, error, sessionId }) {
  if (status === "ok") {
    return null;
  }
  if (status === "polling") {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center">
        <Loader2 className="w-10 h-10 mx-auto text-emerald-500 animate-spin" />
        <h1 className="font-heading text-2xl font-semibold mt-4">Confirming your payment…</h1>
        <p className="text-slate-500 mt-2 text-sm">This usually takes a few seconds.</p>
      </div>
    );
  }
  if (status === "paid") {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-blue-50 p-8 text-center" data-testid="payment-success">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500 text-white flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold mt-5">Payment confirmed 🎉</h1>
        <p className="text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
          Your Publish Assistant is unlocked. It walks you to a live Google profile step-by-step — your answers travel with you, so there's nothing to retype.
        </p>
      </div>
    );
  }
  if (status === "expired") {
    return (
      <ErrorBanner title="Checkout expired" body="Your Stripe session has expired. Head back to payment to try again." />
    );
  }
  if (status === "timeout") {
    return (
      <ErrorBanner title="Still confirming…" body="We couldn’t confirm your payment in time. If you completed the checkout, refresh this page in a moment." />
    );
  }
  if (status === "error") {
    return <ErrorBanner title="Something went wrong" body={error || "Please try again."} />;
  }
  return null;
}

function ErrorBanner({ title, body }) {
  return (
    <div className="rounded-3xl border border-rose-200 bg-rose-50 p-8 text-center">
      <XCircle className="w-10 h-10 mx-auto text-rose-500" />
      <h1 className="font-heading text-2xl font-semibold mt-4 text-rose-900">{title}</h1>
      <p className="text-rose-800/80 mt-2 text-sm max-w-md mx-auto">{body}</p>
      <Link to="/wizard?step=8" className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium bg-slate-900 text-white rounded-full px-5 py-2.5">
        Back to payment
      </Link>
    </div>
  );
}

function ProfileRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className={value ? "text-slate-800 font-medium text-right" : "text-slate-400 italic"}>{value || "—"}</span>
    </div>
  );
}

function ProfileSummary({ state, bt }) {
  const b = state.businessInfo;
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="w-4 h-4 text-emerald-500" />
        <h2 className="font-heading text-lg font-semibold">Your Google profile</h2>
      </div>
      <ProfileRow label="Name" value={b.name} />
      <ProfileRow label="Category" value={bt ? bt.category : ""} />
      <ProfileRow label="Phone" value={b.phone} />
      <ProfileRow label="Address" value={b.address} />
      <ProfileRow label="Website" value={b.website} />
      <ProfileRow label="Service area" value={b.serviceArea} />
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Services</div>
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{state.services || "—"}</p>
      </div>
    </div>
  );
}
